import React, { useEffect, useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Dimensions,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TUTORIALS, hasSeen, markSeen } from '@/lib/tutorials';

interface Props {
  screenKey: string;
}

export default function TutorialOverlay({ screenKey }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [visible, setVisible] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  const tutorial = TUTORIALS[screenKey];

  useEffect(() => {
    if (!tutorial) return;
    let cancelled = false;
    hasSeen(screenKey).then((seen) => {
      if (!cancelled && !seen) {
        setVisible(true);
        Animated.timing(fadeAnim, { toValue: 1, duration: 300, useNativeDriver: true }).start();
      }
    });
    return () => { cancelled = true; };
  }, [screenKey]);

  const dismiss = () => {
    Animated.timing(fadeAnim, { toValue: 0, duration: 200, useNativeDriver: true }).start(() => {
      setVisible(false);
    });
    markSeen(screenKey);
  };

  const handleNext = () => {
    if (!tutorial) return;
    if (stepIndex < tutorial.steps.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      dismiss();
    }
  };

  if (!visible || !tutorial) return null;

  const step = tutorial.steps[stepIndex];
  const isLast = stepIndex === tutorial.steps.length - 1;
  const total = tutorial.steps.length;

  return (
    <Animated.View style={[styles.overlay, { opacity: fadeAnim }]} pointerEvents="auto">
      <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={dismiss} />
      <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>  
        {/* Header */}
        <View style={styles.header}>
          <Text style={[styles.title, { color: c.text }]}>{tutorial.title}</Text>
          <Text style={[styles.counter, { color: c.textTertiary }]}>
            {stepIndex + 1} / {total}
          </Text>
        </View>

        {/* Step dots */}
        <View style={styles.dots}>
          {tutorial.steps.map((_, i) => (
            <View
              key={i}
              style={[
                styles.dot,
                { backgroundColor: i === stepIndex ? c.accent : c.surfaceVariant },
              ]}
            />
          ))}
        </View>

        {/* Hint indicator */}
        {step.hint && (
          <View style={[styles.hintRow, { backgroundColor: c.accent + '15', borderColor: c.accent + '30' }]}>
            <Text style={[styles.hintText, { color: c.accent }]}>{step.hint}</Text>
          </View>
        )}

        {/* Content */}
        <Text style={[styles.stepText, { color: c.textSecondary }]}>{step.text}</Text>

        {/* Buttons */}
        <View style={styles.buttons}>
          <TouchableOpacity onPress={dismiss} style={styles.skipBtn}>
            <Text style={[styles.skipText, { color: c.textTertiary }]}>Skip</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={handleNext}
            style={[styles.nextBtn, { backgroundColor: c.accent }]}
            activeOpacity={0.7}
          >
            <Text style={[styles.nextText, { color: c.accentForeground }]}>
              {isLast ? 'Got it' : 'Next'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 9999,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.6)',
  },
  card: {
    marginHorizontal: 16,
    marginBottom: 32,
    borderRadius: 2,
    borderWidth: 1,
    padding: 24,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
    letterSpacing: -0.3,
  },
  counter: {
    fontSize: 13,
    fontWeight: '600',
  },
  dots: {
    flexDirection: 'row',
    gap: 6,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  hintRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 4,
    borderWidth: 1,
    marginBottom: 10,
    alignSelf: 'flex-start',
  },
  hintText: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  stepText: {
    fontSize: 15,
    lineHeight: 22,
    fontWeight: '500',
  },
  buttons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
  },
  skipBtn: {
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  skipText: {
    fontSize: 14,
    fontWeight: '600',
  },
  nextBtn: {
    paddingVertical: 12,
    paddingHorizontal: 28,
    borderRadius: 0,
  },
  nextText: {
    fontSize: 15,
    fontWeight: '700',
  },
});
